import { PropsWithChildren, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { LOGIN } from 'configs/routeNames';
import {
  getTokenFromLocal,
  getUserFromLocal,
  login,
  logout,
  removeTokenFromLocal,
  removeUserFromLocal,
  saveUserToLocal,
} from './services';
import { updateAbilityFromRole } from 'app/atoms';
import { useIdleTimeout } from 'hooks/useIdleTimeout';

const AuthComponent: React.FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isResolving, setIsResolving] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSettled: () => {
      removeTokenFromLocal();
      removeUserFromLocal();
      navigate(`/${LOGIN}`);
    },
  });

  useEffect(() => {
    const token = getTokenFromLocal();
    const cachedUser = getUserFromLocal();

    if (!token) {
      setIsResolving(false);
      navigate(`/${LOGIN}`);
      return;
    }

    if (cachedUser) {
      queryClient.setQueryData(['auth', 'user-info'], cachedUser);
      updateAbilityFromRole(cachedUser.role);
      setIsAuthenticated(true);
      setIsResolving(false);
      return;
    }

    // Token present but no cached user (e.g. storage was cleared) - re-validate
    // with the backend. This also records a fresh LOGIN audit entry, which is
    // appropriate since it reflects a genuinely new session in this browser.
    login(token)
      .then((user) => {
        saveUserToLocal(user);
        queryClient.setQueryData(['auth', 'user-info'], user);
        updateAbilityFromRole(user.role);
        setIsAuthenticated(true);
      })
      .catch(() => {
        removeTokenFromLocal();
        removeUserFromLocal();
        navigate(`/${LOGIN}`);
      })
      .finally(() => setIsResolving(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleIdleTimeout = () => {
    toast.error('You have been logged out due to inactivity');
    logoutMutation.mutate();
  };

  useIdleTimeout({ onTimeout: handleIdleTimeout, enabled: isAuthenticated });

  if (isResolving) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <Spin size="large" data-testid="auth-loading-spinner" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return children;
};

export default AuthComponent;
